import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Divider,
  Flex,
  FlexItem,
  MenuToggle,
  Select,
  SelectList,
  SelectOption,
  Spinner,
} from '@patternfly/react-core';
import { PencilAltIcon, CheckIcon, TimesIcon } from '@patternfly/react-icons';

const CLEAR_SENTINEL = '__clear__';

export interface InlineEditSelectOption {
  value: string | number;
  label: string;
}

export interface InlineEditSelectProps {
  currentLabel: string | null;
  currentValue: string | number | null;
  options: InlineEditSelectOption[];
  onSave: (newValue: string | number | null) => Promise<void>;
  placeholder?: string;
  ariaLabel?: string;
  /** If true, show a clear option at the top of the dropdown. Default: false */
  clearable?: boolean;
  /** Label for the clear option. Default: "None" */
  clearLabel?: string;
}

function InlineEditSelect({
  currentLabel,
  currentValue,
  options,
  onSave,
  placeholder = '—',
  ariaLabel = 'Edit value',
  clearable = false,
  clearLabel = 'None',
}: InlineEditSelectProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | number | null>(currentValue);
  const [isSaving, setIsSaving] = useState(false);
  const openedByEdit = useRef(false);

  useEffect(() => {
    if (isEditing && !openedByEdit.current) {
      openedByEdit.current = true;
      setIsOpen(true);
    }
  }, [isEditing]);

  const handleEdit = () => {
    setSelectedValue(currentValue);
    openedByEdit.current = false;
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsOpen(false);
    setSelectedValue(currentValue);
  };

  const isClearSelected = selectedValue === null;

  const handleConfirm = async () => {
    if (!clearable && selectedValue === null) return;
    setIsSaving(true);
    try {
      await onSave(isClearSelected ? null : selectedValue);
    } catch {
      // Caller handles error notification; we just revert.
    } finally {
      setIsSaving(false);
      setIsEditing(false);
      setIsOpen(false);
    }
  };

  const onSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    if (value === CLEAR_SENTINEL) {
      setSelectedValue(null);
    } else if (value !== undefined) {
      setSelectedValue(value);
    }
    setIsOpen(false);
  };

  const selectedLabel = isClearSelected
    ? clearLabel
    : (options.find((o) => o.value === selectedValue)?.label ?? placeholder);

  const selectSelected = isClearSelected ? CLEAR_SENTINEL : selectedValue;

  if (!isEditing) {
    return (
      <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}>
        <FlexItem>{currentLabel ?? placeholder}</FlexItem>
        <FlexItem>
          <Button variant="plain" aria-label={ariaLabel} onClick={handleEdit} icon={<PencilAltIcon />} />
        </FlexItem>
      </Flex>
    );
  }

  return (
    <Flex alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsSm' }}>
      <FlexItem>
        <div style={{ width: '200px' }}>
          <Select
            isOpen={isOpen}
            selected={selectSelected}
            onSelect={onSelect}
            onOpenChange={(open) => setIsOpen(open)}
            toggle={(toggleRef) => (
              <MenuToggle
                ref={toggleRef}
                onClick={() => setIsOpen(!isOpen)}
                isExpanded={isOpen}
                isDisabled={isSaving}
                isFullWidth
              >
                {selectedLabel}
              </MenuToggle>
            )}
          >
            <SelectList>
              {clearable && (
                <SelectOption value={CLEAR_SENTINEL} style={{ fontStyle: 'italic' }}>
                  {clearLabel}
                </SelectOption>
              )}
              {clearable && <Divider />}
              {options.map((option) => (
                <SelectOption key={option.value} value={option.value}>
                  {option.label}
                </SelectOption>
              ))}
            </SelectList>
          </Select>
        </div>
      </FlexItem>
      <FlexItem>
        <Button
          variant="plain"
          aria-label="Confirm"
          onClick={handleConfirm}
          isDisabled={isSaving || (!clearable && selectedValue === null)}
          icon={isSaving ? <Spinner size="sm" /> : <CheckIcon />}
        />
      </FlexItem>
      <FlexItem>
        <Button variant="plain" aria-label="Cancel" onClick={handleCancel} isDisabled={isSaving} icon={<TimesIcon />} />
      </FlexItem>
    </Flex>
  );
}

export default InlineEditSelect;
